/**
 * The iOS segmented control, section 8.3's "secondary control that is not a
 * full-width button".
 *
 * Extracted from RequestLesson, which held the only implementation. Lessons had
 * two more — an underline tab bar on mobile and a bordered pill track on
 * desktop — so this is the single source rather than a third and fourth copy.
 *
 *   track      rgba(120,120,128,0.16), radius 100, 32 high, 2px inset
 *   selected   white pill at radius 100, carrying SEG_SHADOW
 *   labels     Subhead 15/20; selected 600, unselected 500 at
 *              rgba(60,60,67,0.6)
 *   target     32 visible, expanded to 44 by a transparent vertical ::after,
 *              per 8.4 — expand the target, do not inflate the visual
 *   press      no text selection and no callout: a long press in iOS Safari
 *              otherwise raised the selection loupe over the control, which
 *              the 27 Oct walk caught as a stray "all." (the "Call" of
 *              Guidance Call, magnified; room walk B03)
 */

const SEG_TRACK =
  'flex h-8 w-full select-none items-center rounded-[100px] bg-[rgba(120,120,128,0.16)] p-[2px] [-webkit-touch-callout:none]';

const SEG_BASE =
  "relative after:absolute after:inset-x-0 after:-inset-y-2 after:content-[''] " +
  'flex h-full flex-1 items-center justify-center gap-1.5 rounded-[100px] ' +
  'text-[15px] leading-[20px] focus:outline-none focus-visible:ring-2 focus-visible:ring-db-accent/40';

const SEG_SELECTED = `${SEG_BASE} bg-white font-semibold text-black`;
const SEG_UNSELECTED = `${SEG_BASE} ios-press font-medium text-[rgba(60,60,67,0.6)]`;

/**
 * The track is rgba(120,120,128,0.16) over white, which resolves to about
 * #E9E9EB — 1.22:1 against the white pill. Nothing separates the two but this
 * shadow, so it carries the whole lift.
 *
 * A 0.5px rgba(0,0,0,0.04) border was tried alongside it and dropped: rendered
 * at 3x it reads as a hairline outlining the pill rather than lifting it. An
 * outline says "this shape is drawn here"; a shadow says "this shape is on
 * top". The second is what a segmented control needs.
 *
 * History: 0 3px 8px rgba(0,0,0,0.12) / 0 3px 1px rgba(0,0,0,0.04) has been
 * tried twice and rejected twice — the second layer is 1px of blur at 4%, which
 * barely registers even at 3x. Recorded so it is not proposed a third time.
 */
const SEG_SHADOW = { boxShadow: '0 3px 8px rgba(0,0,0,0.18), 0 1px 3px rgba(0,0,0,0.10)' };

export interface Segment<T extends string> {
  value: T;
  label: string;
  /** Optional count shown after the label. Hidden at zero. */
  badge?: number;
  /** Forwarded to data-testid, so existing e2e selectors keep working. */
  testId?: string;
}

interface SegmentedControlProps<T extends string> {
  segments: Segment<T>[];
  value: T;
  onChange: (value: T) => void;
  /** Names the group for screen readers, e.g. "Lesson filter". */
  ariaLabel?: string;
  className?: string;
}

export default function SegmentedControl<T extends string>({
  segments,
  value,
  onChange,
  ariaLabel,
  className = '',
}: SegmentedControlProps<T>) {
  return (
    <div className={`${SEG_TRACK} ${className}`} role="group" aria-label={ariaLabel}>
      {segments.map((segment) => {
        const selected = segment.value === value;
        return (
          <button
            key={segment.value}
            type="button"
            data-testid={segment.testId}
            aria-pressed={selected}
            onClick={() => onChange(segment.value)}
            className={selected ? SEG_SELECTED : SEG_UNSELECTED}
            style={selected ? SEG_SHADOW : undefined}
          >
            {segment.label}
            {segment.badge != null && segment.badge > 0 && (
              <span
                className="flex items-center justify-center rounded-[100px] text-[12px] leading-[16px] font-semibold tabular-nums"
                /* Light, not a filled dark dot. A count is a quantity, not a
                   status: a solid biscayne or grey circle reads as a badge
                   demanding attention, which is what an unread marker looks
                   like. The neutral fill 8.3 already gives secondary controls
                   says "here is a number" and nothing more. */
                style={{
                  minWidth: 18,
                  height: 18,
                  padding: '0 5px',
                  background: 'rgba(120,120,128,0.16)',
                  color: '#1C1C1C',
                }}
              >
                {segment.badge}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
